import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import en from './locales/en.json'
import fr from './locales/fr.json'
import { DEFAULT_LOCALE, LOCALES, resolveInitialLocale, storeLocale, type Locale } from './locale'

/**
 * Every string in the panel goes through here.
 *
 * Retrofitting localization means auditing every component for literals, which nobody ever
 * finishes. Doing it from the start costs one function call per string.
 */
export const defaultNS = 'translation'
export const resources = {
  en: { translation: en },
  fr: { translation: fr },
} as const

const initial = resolveInitialLocale()

await i18n.use(initReactI18next).init({
  resources,
  lng: initial,
  /*
   * English is the source catalogue, so a key added to it but not yet translated renders as
   * English rather than as the raw key. `supportedLngs` keeps a stray tag from a browser or a
   * stale stored value from selecting a catalogue that does not exist.
   */
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: LOCALES,
  defaultNS,
  interpolation: {
    // React already escapes everything it renders; escaping again would double-encode an
    // apostrophe in a world title.
    escapeValue: false,
  },
  returnNull: false,
  // A missing key must be loud in development. In production it is left alone: the fallback
  // renders the key itself, which is ugly but not worth a console error on every paint.
  saveMissing: import.meta.env.DEV,
  ...(import.meta.env.DEV
    ? {
        missingKeyHandler: (_lngs: readonly string[], _ns: string, key: string) => {
          console.error(`[i18n] missing key: ${key}`)
        },
      }
    : {}),
})

/*
 * Screen readers announce in the language the document declares, and CSS hyphenation and
 * quotation rules key off it too. Without this the panel would say `lang="en"` while rendering
 * French.
 */
document.documentElement.lang = i18n.resolvedLanguage ?? initial

/** Switch language, remember the choice, and keep `<html lang>` honest. */
export async function setLocale(locale: Locale): Promise<void> {
  await i18n.changeLanguage(locale)
  storeLocale(locale)
  document.documentElement.lang = locale
}

export default i18n
